'use client';

import { useCallback } from 'react';
import { TransactionSignature, PublicKey } from '@solana/web3.js';
import { BN } from '@drift-labs/sdk';
import {
	Vault,
	VaultClient,
	VaultDepositor,
	VaultDepositorAccount,
	VAULT_PROGRAM_ID,
} from '@drift-labs/vaults-sdk';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';
import { useWaitForTxConfirm } from '../transactions/useWaitForTxConfirm';
import { mapTxParamsToVaultTxParams } from './utils';
import useDriftClient from 'src/hooks/useDriftClient';

export const useDepositVault = () => {
	const driftClient = useDriftClient();

	const getTxParams = useGetTxParams();
	const { waitForTxConfirm } = useWaitForTxConfirm();

	const depositVault = useCallback(
		async (
			vault: Vault,
			vaultClient: VaultClient,
			amount: BN,
			vaultDepositor?: VaultDepositor
		): Promise<TransactionSignature | undefined> => {
			if (!vaultDepositor && vault.permissioned) {
				notify({
					type: 'error',
					message: 'You are not authorized to deposit into this vault',
				});
				return undefined;
			}

			const authority = driftClient?.authority as PublicKey;
			const toastId = 'depositVault';

			try {
				notify({
					type: 'awaiting',
					message: 'Awaiting transaction confirmation',
					id: toastId,
					showUntilCancelled: true,
				});

				let txSig: TransactionSignature;

				if (!vaultDepositor) {
					if (!authority) {
						notify({
							type: 'error',
							message: 'Please connect your wallet to deposit into this vault',
							id: toastId,
							updatePrevious: true,
							showUntilCancelled: false,
						});
						return undefined;
					}

					const vaultDepositorPubkey = VaultDepositorAccount.getAddressSync(
						VAULT_PROGRAM_ID,
						vault.pubkey,
						authority
					);

					txSig = await vaultClient.deposit(
						vaultDepositorPubkey,
						amount,
						{
							authority,
							vault: vault.pubkey,
						},
						mapTxParamsToVaultTxParams(getTxParams())
					);
				} else {
					txSig = await vaultClient.deposit(
						vaultDepositor.pubkey,
						amount,
						undefined,
						mapTxParamsToVaultTxParams(getTxParams())
					);
				}

				await waitForTxConfirm(txSig);

				notify({
					type: 'success',
					message: 'Deposit successful.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return txSig;
			} catch (err: any) {
				console.error(err);
				notify({
					type: 'error',
					message: 'There was an error depositing into this vault.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});
				return undefined;
			}
		},
		[driftClient, getTxParams, waitForTxConfirm]
	);

	return { depositVault };
};
